import { useState, useEffect } from "react";
import { apiClient } from "../../services/api";

// Known WiZ bulbs to preload in the dropdown (MAC = 12-hex lowercase, no separators).
export const KNOWN_BULBS = [
  { mac: "d8a01104c2f0", name: "Lámpara derecha 1" },
  { mac: "d8a01104c458", name: "Lámpara derecha 2" },
  { mac: "d8a01104bd8a", name: "Lámpara derecha 3" },
  { mac: "d8a01104876c", name: "Lámpara izquierda 1" },
  { mac: "d8a01104c0a6", name: "Lámpara izquierda 2" },
  { mac: "d8a011048eb8", name: "Lámpara izquierda 3" },
  { mac: "d8a011227040", name: "Luz Tele" },
  { mac: "d8a011489762", name: "Sillones" },
];

function normalizeMac(raw) {
  return (raw || "").toLowerCase().replace(/[^0-9a-f]/g, "").slice(0, 12);
}

function LightEditor({ light, roomName, onUpdate, onDelete, onDeselect }) {
  const [label, setLabel] = useState(light.label || "");
  const [pickMac, setPickMac] = useState("");
  const [manualMac, setManualMac] = useState("");
  const [manualName, setManualName] = useState("");
  const [importOpen, setImportOpen] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importError, setImportError] = useState(null);
  const [wizDevices, setWizDevices] = useState([]);
  const [manualKind, setManualKind] = useState("bulb");

  useEffect(() => {
    setLabel(light.label || "");
  }, [light.id, light.label]);

  const handleKeyDown = (e) => {
    if (e.key === "Enter") e.target.blur();
  };

  const bulbs = light.bulbs || [];

  const addBulb = (mac, name, kind = "bulb") => {
    const m = normalizeMac(mac);
    if (m.length !== 12) return;
    if (bulbs.some((b) => b.mac === m)) return; // no duplicates
    onUpdate({ bulbs: [...bulbs, { mac: m, name: name || m, kind }] });
  };

  const addFromDropdown = () => {
    const known = KNOWN_BULBS.find((b) => b.mac === pickMac);
    if (known) addBulb(known.mac, known.name, "bulb");
    setPickMac("");
  };

  const addManual = () => {
    addBulb(manualMac, manualName.trim(), manualKind);
    setManualMac("");
    setManualName("");
  };

  const removeBulb = (mac) => {
    onUpdate({ bulbs: bulbs.filter((b) => b.mac !== mac) });
  };

  const openImport = async () => {
    const next = !importOpen;
    setImportOpen(next);
    if (!next || wizDevices.length > 0) return;
    setImporting(true);
    setImportError(null);
    try {
      const data = await apiClient.get("/devices");
      const items = (data.items || data.devices || [])
        .filter((d) => !d.type || d.type === "bulb" || d.type === "strip" || d.type === "plug")
        .map((d) => ({
          mac: normalizeMac(d.mac),
          name: d.name || d.mac,
          room: d.room,
          kind: d.type === "plug" ? "plug" : "bulb",
        }))
        .filter((d) => d.mac.length === 12);
      setWizDevices(items);
      if (items.length === 0) setImportError("No WiZ devices found in the registry.");
    } catch (e) {
      console.error("WiZ import failed:", e);
      setImportError("Couldn't reach the device registry.");
    } finally {
      setImporting(false);
    }
  };

  const addAllFromWiz = () => {
    const existing = new Set(bulbs.map((b) => b.mac));
    const additions = wizDevices
      .filter((d) => !existing.has(d.mac))
      .map((d) => ({ mac: d.mac, name: d.name, kind: d.kind || "bulb" }));
    if (additions.length === 0) return;
    onUpdate({ bulbs: [...bulbs, ...additions] });
  };

  const usedMacs = new Set(bulbs.map((b) => b.mac));
  const available = KNOWN_BULBS.filter((b) => !usedMacs.has(b.mac));

  return (
    <div className="room-editor">
      <div className="room-editor-header">
        <h3>Light Editor</h3>
        <button className="btn-icon" onClick={onDeselect} title="Deselect">
          ✕
        </button>
      </div>

      <div className="room-editor-field">
        <label>Label</label>
        <input
          type="text"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          onBlur={() => onUpdate({ label: label.trim() || "Light" })}
          onKeyDown={handleKeyDown}
        />
      </div>

      <div className="room-editor-field">
        <label>Room</label>
        <div className="readonly-field">{roomName || "— (outside rooms)"}</div>
      </div>

      <label className="toolbar-toggle path-toggle">
        <input
          type="checkbox"
          checked={light.sensorManaged !== false}
          onChange={(e) => onUpdate({ sensorManaged: e.target.checked })}
        />
        Managed by presence sensors
      </label>
      <p className="editor-hint">
        When off, presence sensors won't switch this light automatically.
      </p>

      <div className="node-section">
        <div className="node-section-head">
          <span>Bulbs ({bulbs.length})</span>
          <button className="btn-mini" onClick={openImport} title="Import bulbs from WiZ">
            {importOpen ? "Close" : "⤓ WiZ"}
          </button>
        </div>

        {importOpen && (
          <div className="wiz-import">
            {importing && <p className="editor-hint">Loading WiZ devices…</p>}
            {importError && <p className="form-error">{importError}</p>}
            {!importing && wizDevices.length > 0 && (
              <>
                <div className="wiz-import-head">
                  <span>{wizDevices.length} WiZ bulb{wizDevices.length === 1 ? "" : "s"}</span>
                  <button className="btn-mini" onClick={addAllFromWiz}>Add all</button>
                </div>
                {wizDevices.map((d) => {
                  const used = bulbs.some((b) => b.mac === d.mac);
                  return (
                    <div key={d.mac} className="bulb-row">
                      <div className="bulb-info">
                        <span className="bulb-name">
                          {d.kind === "plug" ? "🔌 " : "💡 "}
                          {d.name}
                        </span>
                        <span className="bulb-mac">
                          {d.mac}
                          {d.room ? ` · ${d.room}` : ""}
                          {d.kind === "plug" ? " · on/off only" : ""}
                        </span>
                      </div>
                      <button
                        className="btn-mini"
                        disabled={used}
                        onClick={() => addBulb(d.mac, d.name, d.kind || "bulb")}
                      >
                        {used ? "✓" : "Add"}
                      </button>
                    </div>
                  );
                })}
              </>
            )}
          </div>
        )}

        {bulbs.length === 0 && <p className="editor-hint">No bulbs yet — add one below.</p>}

        {bulbs.map((b) => (
          <div key={b.mac} className="bulb-row">
            <div className="bulb-info">
              <span className="bulb-name">
                {b.kind === "plug" ? "🔌 " : "💡 "}
                {b.name}
              </span>
              <span className="bulb-mac">
                {b.mac}
                {b.kind === "plug" ? " · on/off only" : ""}
              </span>
            </div>
            <button className="btn-mini btn-danger" onClick={() => removeBulb(b.mac)}>
              ✕
            </button>
          </div>
        ))}

        {available.length > 0 && (
          <div className="add-bulb-row">
            <select value={pickMac} onChange={(e) => setPickMac(e.target.value)}>
              <option value="">Known bulb…</option>
              {available.map((b) => (
                <option key={b.mac} value={b.mac}>
                  {b.name}
                </option>
              ))}
            </select>
            <button className="btn-mini" disabled={!pickMac} onClick={addFromDropdown}>
              Add
            </button>
          </div>
        )}

        <div className="add-bulb-manual">
          <select value={manualKind} onChange={(e) => setManualKind(e.target.value)} title="Device kind">
            <option value="bulb">💡 Bulb</option>
            <option value="plug">🔌 Plug</option>
          </select>
          <input
            type="text"
            placeholder="MAC (12 hex)"
            value={manualMac}
            onChange={(e) => setManualMac(e.target.value)}
          />
          <input
            type="text"
            placeholder="Name (optional)"
            value={manualName}
            onChange={(e) => setManualName(e.target.value)}
          />
          <button className="btn-mini" disabled={normalizeMac(manualMac).length !== 12} onClick={addManual}>
            Add
          </button>
        </div>
      </div>

      <p className="editor-hint">Drag the light on the map to position it.</p>

      <div className="room-editor-actions">
        <button className="btn-secondary btn-danger" onClick={onDelete}>
          Delete Light
        </button>
      </div>
    </div>
  );
}

export default LightEditor;
